/**
 * AuditLogView.tsx
 *
 * Chronological vertical timeline of every event recorded for a run.
 *
 * When a DAG node is selected (useObservabilityStore.selectedStepId), only
 * events for that step are shown; otherwise the full run timeline is rendered.
 *
 * Each timeline entry shows:
 *   • Colour-coded event dot (by event-type family)
 *   • Human-readable event name
 *   • Absolute timestamp + relative age (on hover)
 *   • Step reference chip (if the event is step-scoped)
 *   • "Raw Data" toggle → expands the JSON payload inline
 *
 * Data source: GET /v1/runs/{runId}/events?limit=200
 * Events are fetched fresh on mount and when runId changes; no store caching
 * is needed because this view is opened intentionally rather than polled.
 */

import { useCallback, useEffect, useState } from 'react'
import { useObservabilityStore } from '../store/useObservabilityStore'
import { api, apiErrorMessage } from '../api/client'

// ── Types ──────────────────────────────────────────────────────────────────────

interface RunEvent {
  id:         string
  runId:      string
  stepId?:    string
  sequenceNo: number
  eventType:  string
  payload:    Record<string, unknown>
  occurredAt: string
}

interface EventsPage {
  items: RunEvent[]
  page:  { nextCursor?: string }
}

// ── Event-type helpers ────────────────────────────────────────────────────────

function eventFamily(eventType: string): string {
  return eventType.split('.')[0] ?? 'unknown'
}

const FAMILY_COLOR: Record<string, string> = {
  run:      '#38bdf8',  // sky  — run lifecycle
  step:     '#a78bfa',  // violet — step lifecycle
  approval: '#fbbf24',  // amber — HITL events
  error:    '#f87171',  // red
  usage:    '#34d399',  // emerald — cost / token events
  trace:    '#94a3b8',  // slate
}

function eventColor(eventType: string): string {
  const family = eventFamily(eventType)
  if (eventType.includes('fail') || eventType.includes('error')) return '#f87171'
  if (eventType.includes('succeed') || eventType.includes('complet') || eventType.includes('approved')) return '#4ade80'
  return FAMILY_COLOR[family] ?? '#64748b'
}

function formatEventType(eventType: string): string {
  // "step.created" → "Step Created"
  // "approval.requested" → "Approval Requested"
  return eventType
    .split('.')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month:   'short',
      day:     'numeric',
      hour:    '2-digit',
      minute:  '2-digit',
      second:  '2-digit',
      hour12:  false,
    })
  } catch {
    return iso
  }
}

function relativeAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 0)          return 'just now'
  if (diff < 60_000)     return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3_600_000)  return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}

// ── JSON expander ─────────────────────────────────────────────────────────────

function RawData({ payload }: { payload: Record<string, unknown> }) {
  const json = JSON.stringify(payload, null, 2)
  return (
    <pre className="mt-2 px-3 py-[10px] bg-[rgba(6,10,16,0.6)] border border-[rgba(148,163,184,0.1)] rounded-lg font-mono text-[10.5px] leading-[1.6] text-[#8fa3bd] overflow-x-auto whitespace-pre max-h-[260px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.1)_transparent] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar]:h-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(148,163,184,0.12)] [&::-webkit-scrollbar-thumb]:rounded-sm">
      {json}
    </pre>
  )
}

// ── Single timeline entry ─────────────────────────────────────────────────────

function TimelineEntry({
  event,
  isLast,
}: {
  event:  RunEvent
  isLast: boolean
}) {
  const [rawOpen, setRawOpen] = useState(false)
  const hasPayload = event.payload && Object.keys(event.payload).length > 0
  const color = eventColor(event.eventType)

  return (
    <div className="flex gap-0 px-4 pl-[14px]">
      {/* Left rail: dot + connector line */}
      <div className="flex flex-col items-center flex-shrink-0 w-5 pt-0.5" aria-hidden="true">
        <span
          className="block w-[9px] h-[9px] rounded-full flex-shrink-0 relative z-[1]"
          style={{ background: color, boxShadow: `0 0 0 3px ${color}22` }}
        />
        {!isLast && (
          <span className="block w-px flex-1 min-h-[14px] bg-[rgba(148,163,184,0.1)] mt-1" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-[18px] pl-[10px]">
        {/* Top row */}
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <span className="text-[12.5px] font-bold tracking-[0.01em]" style={{ color }}>
            {formatEventType(event.eventType)}
          </span>
          <span
            className="text-[10px] text-[#3a5068] whitespace-nowrap tabular-nums flex gap-1 items-baseline"
            title={new Date(event.occurredAt).toISOString()}
          >
            <span className="text-[#4e6480]">{formatTimestamp(event.occurredAt)}</span>
            <span className="text-[#2d3f52] italic">({relativeAge(event.occurredAt)})</span>
          </span>
        </div>

        {/* Step reference chip */}
        {event.stepId && (
          <div className="inline-flex items-center gap-[5px] mt-1 bg-[rgba(167,139,250,0.08)] border border-[rgba(167,139,250,0.16)] rounded-[6px] px-[7px] py-[1px] pl-[5px]">
            <span className="text-[9px] font-[800] tracking-[0.1em] uppercase text-[#4e6480]" aria-label="Step">step</span>
            <code className="font-mono text-[10px] text-[#a78bfa]">{event.stepId.slice(0, 12)}…</code>
          </div>
        )}

        {/* Sequence number */}
        <div className="text-[9.5px] text-[#2d3f52] mt-[3px] tabular-nums">#{event.sequenceNo}</div>

        {/* Raw data toggle */}
        {hasPayload && (
          <button
            className={`inline-flex items-center gap-1 mt-[6px] bg-transparent border border-[rgba(148,163,184,0.12)] rounded-[5px] text-[#4e6480] font-[inherit] text-[10.5px] font-bold px-2 py-0.5 cursor-pointer transition-all duration-[120ms] hover:bg-[rgba(148,163,184,0.07)] hover:text-[#8fa3bd] hover:border-[rgba(148,163,184,0.2)]${rawOpen ? ' bg-[rgba(148,163,184,0.07)] text-[#8fa3bd] border-[rgba(148,163,184,0.18)]' : ''}`}
            onClick={() => setRawOpen((v) => !v)}
            aria-expanded={rawOpen}
            aria-label={rawOpen ? 'Hide raw data' : 'Show raw data'}
          >
            <span className="text-[9px]" aria-hidden="true">
              {rawOpen ? '▾' : '▸'}
            </span>
            Raw data
          </button>
        )}
        {rawOpen && hasPayload && <RawData payload={event.payload} />}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export type AuditLogViewProps = {
  runId: string
}

export function AuditLogView({ runId }: AuditLogViewProps) {
  const selectedStepId = useObservabilityStore((s) => s.selectedStepId)

  const [events,    setEvents]    = useState<RunEvent[]>([])
  const [nextCursor,setNextCursor]= useState<string | null>(null)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  // Load events — append=true for pagination, false for fresh load.
  const loadEvents = useCallback(async (cursor?: string, append = false) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: '200' })
      if (cursor) params.set('cursor', cursor)
      const data = await api.get<EventsPage>(
        `/v1/runs/${encodeURIComponent(runId)}/events?${params.toString()}`,
      )
      setEvents((prev) => append ? [...prev, ...(data.items ?? [])] : (data.items ?? []))
      setNextCursor(data.page?.nextCursor ?? null)
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to load events'))
    } finally {
      setLoading(false)
    }
  }, [runId])

  // Fresh load whenever runId changes.
  useEffect(() => {
    setEvents([])
    setNextCursor(null)
    void loadEvents()
  }, [loadEvents])

  // Client-side filter: if a step is selected, show only events for it
  // (plus run-level events that have no stepId).
  const visible = selectedStepId
    ? events.filter((e) => !e.stepId || e.stepId === selectedStepId)
    : events

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <section className="flex flex-col h-full overflow-hidden bg-transparent">
      {/* Header */}
      <div className="flex items-center justify-between px-[18px] pt-[14px] pb-3 flex-shrink-0 border-b border-[rgba(148,163,184,0.08)] gap-3">
        <div className="flex items-center gap-[10px] min-w-0">
          <h2 className="text-[10.5px] font-[800] tracking-[0.14em] uppercase text-[#3a5068] m-0 flex-shrink-0">
            Audit Log
          </h2>
          {selectedStepId ? (
            <div className="flex items-center gap-[6px] bg-[rgba(167,139,250,0.08)] border border-[rgba(167,139,250,0.2)] rounded-lg px-2 py-[3px] pl-[6px] text-[11px]">
              <span className="text-[#8fa3bd]">Filtered to step</span>
              <code className="font-mono text-[10.5px] text-[#a78bfa]">{selectedStepId.slice(0, 12)}…</code>
              <button
                className="bg-transparent border-none cursor-pointer text-[#64748b] font-[inherit] text-[10.5px] pl-1 transition-colors duration-[130ms] hover:text-[#f87171] p-0"
                onClick={() => useObservabilityStore.getState().selectStep(undefined)}
                title="Show all events"
              >
                ✕ Clear
              </button>
            </div>
          ) : (
            <span className="text-[10.5px] text-[#2d3f52] italic">Click a DAG node to filter by step</span>
          )}
        </div>
        <div className="text-[11px] text-[#3a5068] tabular-nums flex-shrink-0">
          {visible.length} event{visible.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Loading skeleton */}
      {loading && events.length === 0 && (
        <div className="flex items-center gap-[10px] px-[18px] py-6 text-[12px] text-[#3a5068]">
          <span className="alv-spinner" aria-label="Loading events…" />
          Loading events…
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-4 my-[10px] px-3 py-[10px] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-[10px] text-[#f87171] text-[11.5px] flex items-center gap-2 flex-wrap">
          <span aria-hidden="true">⚠</span> {error}
          <button
            className="bg-transparent border-none text-[#f87171] font-[inherit] text-[11.5px] cursor-pointer underline p-0 ml-auto"
            onClick={() => void loadEvents()}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && visible.length === 0 && (
        <div className="px-[18px] py-8 text-center text-[12px] text-[#2d3f52]">
          {selectedStepId
            ? 'No events recorded for this step.'
            : 'No events recorded for this run.'}
        </div>
      )}

      {/* Timeline */}
      {visible.length > 0 && (
        <div
          className="flex-1 overflow-y-auto pt-[14px] pb-2 [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.12)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(148,163,184,0.12)] [&::-webkit-scrollbar-thumb]:rounded-sm"
          role="list"
          aria-label="Run event timeline"
        >
          {visible.map((event, idx) => (
            <div key={event.id} role="listitem">
              <TimelineEntry
                event={event}
                isLast={idx === visible.length - 1}
              />
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      {nextCursor && !loading && (
        <div className="px-4 pt-[6px] pb-3 flex justify-center flex-shrink-0">
          <button
            className="w-full inline-flex items-center justify-center gap-[6px] bg-[rgba(148,163,184,0.06)] border border-[rgba(148,163,184,0.12)] rounded-lg text-[#8fa3bd] font-[inherit] text-[11.5px] font-bold px-[14px] py-[6px] cursor-pointer transition-colors duration-[130ms] hover:bg-[rgba(148,163,184,0.11)] hover:text-[#c8d9ee]"
            onClick={() => void loadEvents(nextCursor, true)}
          >
            Load older events
          </button>
        </div>
      )}

      {/* In-progress spinner appended while loading more */}
      {loading && events.length > 0 && (
        <div className="px-[18px] py-[10px] flex justify-center items-center gap-[10px] text-[12px] text-[#3a5068]">
          <span className="alv-spinner alv-spinner--sm" aria-hidden="true" />
        </div>
      )}
    </section>
  )
}
